/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */
import type { PopperPlacementType, PopperProps } from "@mui/material/Popper";

import { ARROW_SIZE, VIEWPORT_MARGIN } from "./popperConstants";

type PopperModifier = NonNullable<PopperProps["modifiers"]>[number];

export function buildArrowModifier(
  arrowRef: HTMLSpanElement | null,
  arrowAnchorEl: HTMLElement | null
): PopperModifier {
  return {
    name: "arrow",
    enabled: !arrowAnchorEl && !!arrowRef,
    options: { element: arrowRef },
  };
}

function getAnchorRect(anchorEl: PopperProps["anchorEl"]): DOMRect | null {
  const anchor = typeof anchorEl === "function" ? anchorEl() : anchorEl;
  if (
    !anchor ||
    typeof (anchor as { getBoundingClientRect?: unknown })
      .getBoundingClientRect !== "function"
  ) {
    return null;
  }
  return (
    anchor as { getBoundingClientRect: () => DOMRect }
  ).getBoundingClientRect();
}

/**
 * Offset modifier that, when an `arrowAnchorEl` is supplied, shifts the
 * popover along the cross-axis so the launcher icon's vertical centre
 * always falls within the popover's vertical range — instead of padding
 * the popover with empty space to reach the icon.
 */
export function buildOffsetModifier(
  anchorEl: PopperProps["anchorEl"],
  arrowAnchorEl: HTMLElement | null
): PopperModifier {
  return {
    name: "offset",
    options: {
      offset: ({ popper }: { popper: { height: number } }) => {
        const distance = ARROW_SIZE - 4;
        if (!arrowAnchorEl) return [0, distance];

        const anchorRect = getAnchorRect(anchorEl);
        if (!anchorRect || popper.height <= 0) return [0, distance];

        const iconRect = arrowAnchorEl.getBoundingClientRect();
        const iconCenter = iconRect.top + iconRect.height / 2;
        const naturalTop = anchorRect.top;
        const naturalBottom = naturalTop + popper.height;
        const margin = ARROW_SIZE + 4;

        if (iconCenter > naturalBottom - margin) {
          return [iconCenter - naturalBottom + margin, distance];
        }
        if (iconCenter < naturalTop + margin) {
          return [iconCenter - naturalTop - margin, distance];
        }
        return [0, distance];
      },
    },
  };
}

/**
 * preventOverflow tuned to keep the popover pinned to the caller's
 * requested placement. We disable mainAxis shifting so the popover never
 * drifts back over the sidebar (or away from its requested side), and rely
 * on altAxis shifting to keep it inside the viewport along the cross axis —
 * vertically for right/left placements, horizontally for top/bottom ones.
 */
export function buildPreventOverflowModifier(
  _placement: PopperPlacementType
): PopperModifier {
  return {
    name: "preventOverflow",
    options: {
      padding: VIEWPORT_MARGIN,
      rootBoundary: "viewport",
      mainAxis: false,
      altAxis: true,
      tether: false,
    },
  };
}

/**
 * Flip modifier tuned so the popover can dodge a viewport edge without ever
 * drifting back over the sidebar.
 *
 * - Horizontal placements (right/left): only flip the alignment
 *   (`-start` <-> `-end`) so a launcher near the bottom (or top) of the
 *   screen opens away from that edge. The opposite side is never offered as
 *   a fallback, so the popover can't jump sideways over the sidebar.
 * - Vertical placements (top/bottom): flip only to the opposite side to
 *   dodge the top/bottom edge.
 */
export function buildFlipModifier(
  placement: PopperPlacementType
): PopperModifier {
  if (placement.startsWith("right") || placement.startsWith("left")) {
    const fallback = placement.endsWith("-start")
      ? placement.replace("-start", "-end")
      : placement.endsWith("-end")
      ? placement.replace("-end", "-start")
      : null;
    return {
      name: "flip",
      options: {
        fallbackPlacements: fallback ? [fallback as PopperPlacementType] : [],
      },
    };
  }
  const opposite = placement.startsWith("top")
    ? placement.replace("top", "bottom")
    : placement.replace("bottom", "top");
  return {
    name: "flip",
    options: { fallbackPlacements: [opposite as PopperPlacementType] },
  };
}
